import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@praesto/ui-kit";

/**
 * Foreground push listener. When the PWA is focused, FCM delivers the message to
 * `onMessage` instead of the background service worker — so there is nothing
 * visible unless we surface it. This mounts an `onMessage` listener (only when
 * push is permitted + supported) and pushes a click-to-open toast through the
 * shared ToastProvider. Renders nothing itself. The inbox bell still updates
 * live regardless; this is the in-app surface for "it just arrived".
 */
export function ForegroundPushToast() {
  const navigate = useNavigate();
  const { show } = useToast();

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      try {
        if (typeof window === "undefined" || !("Notification" in window)) return;
        if (Notification.permission !== "granted") return;
        const [{ getMessaging, isSupported, onMessage }, { getApp }] = await Promise.all([
          import("firebase/messaging"),
          import("firebase/app"),
        ]);
        if (cancelled || !(await isSupported())) return;
        const messaging = getMessaging(getApp("praesto"));
        unsub = onMessage(messaging, (payload) => {
          const n = payload.notification ?? {};
          const deepLink = payload.data?.["deepLink"];
          show(n.body ?? "", {
            title: n.title ?? "Notifica",
            duration: 6000,
            ...(deepLink
              ? {
                  onClick: () => {
                    try {
                      const url = new URL(deepLink, window.location.origin);
                      void navigate({
                        to: url.pathname as never,
                        search: Object.fromEntries(url.searchParams.entries()) as never,
                      });
                    } catch {
                      // Ignore malformed deep links — the toast simply dismisses.
                    }
                  },
                }
              : {}),
          });
        });
      } catch {
        // Messaging unavailable — no foreground toast; the inbox bell still works.
      }
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [navigate, show]);

  return null;
}
